import React from 'react';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

import LoadingButton from '@mui/lab/LoadingButton';
import DownloadIcon from '@mui/icons-material/Download';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import '../../../styles/AggReport.css';
import { refreshToken } from '../../../globals/GlobalFunctions';

function AggReport() {
    const [reportMonth, setReportMonth] = React.useState<Dayjs | null>(null);
    const [claimDetail, setClaimDetail] = React.useState(false);
    const [loadingReport, setLoadingReport] = React.useState(false);

    const [validationError, setValidationError] = React.useState(false);
    const [validationText, setValidationText] = React.useState('');

    const [successAlert, setSuccessAlert] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState(false);
    const [errorText, setErrorText] = React.useState('There was an error generating AGG report.');

    const token = localStorage.getItem('jwt');
    const baseURL = localStorage.getItem('baseURL') || '';

    const minMonth = dayjs('2020-01-01');
    const maxMonth = dayjs().startOf('month');

    const getMonthForApi = () => {
        return reportMonth ? reportMonth.format('YYYY-MM') : '';
    };

    const validateRequest = () => {
        if (!reportMonth) {
            setValidationText('Month is required.');
            setValidationError(true);
            return false;
        }

        return true;
    };

    const generateAggReport = async () => {
        if (!validateRequest()) {
            return;
        }

        try {
            setLoadingReport(true);
            setErrorAlert(false);
            setSuccessAlert(false);

            refreshToken();

            const formattedMonth = getMonthForApi();

            // claim detail returns a zip of the agg sheet plus a claim report per filing contract
            const requestUrl = claimDetail
                ? `${baseURL}reports/getAggReport/${formattedMonth}?generateClaimDetailReports=true`
                : `${baseURL}reports/getAggReport/${formattedMonth}`;

            const response = await axios.get(
                requestUrl,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const { fileName, contentType, data } = response.data;

            const binaryString = atob(data);
            const len = binaryString.length;
            const bytes = new Uint8Array(len);

            for (let i = 0; i < len; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }

            const blob = new Blob([bytes], { type: contentType });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(url);

            setSuccessAlert(true);
        } catch (error: any) {
            console.error('AGG report error:', error);

            if (error?.response) {
                setErrorText(`Backend error: ${error.response.status} ${error.response.statusText}`);
            } else if (error?.request) {
                setErrorText('No response from backend. Make sure the API is running.');
            } else {
                setErrorText('There was an error generating the AGG report.');
            }

            setErrorAlert(true);
        } finally {
            setLoadingReport(false);
        }
    };

    return (
        <div className="aggReportPageWrap">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <div className="aggReportCard">
					<span className="aggReportTitle">
						AGG Report
					</span>

                    <div className="aggReportField">
                        <span className="aggReportLabel">Report Date</span>

                        <DatePicker
                            views={['year', 'month']}
                            format="MM YYYY"
                            value={reportMonth}
                            onChange={(newValue) => setReportMonth(newValue)}
                            minDate={minMonth}
                            maxDate={maxMonth}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    fullWidth: true,
                                    className: 'aggReportDateField',
                                },
                            }}
                        />
                    </div>

                    <div className="aggReportCheckboxRow">
                        <FormControlLabel
                            className="aggReportCheckboxLabel"
                            control={
                                <Checkbox
                                    checked={claimDetail}
                                    onChange={(event) => setClaimDetail(event.target.checked)}
                                    disabled={loadingReport}
                                    size="small"
                                    className="aggReportCheckbox"
                                />
                            }
                            label="Generate Claim Detail Reports"
                        />
                    </div>

                    <LoadingButton
                        variant="contained"
                        onClick={generateAggReport}
                        loading={loadingReport}
                        startIcon={<DownloadIcon />}
                        className="aggReportButton"
                    >
                        Generate AGG Report
                    </LoadingButton>

                    {successAlert && (
                        <Alert severity="success" className="aggReportAlert">
                            <AlertTitle>Success</AlertTitle>
                            AGG report request completed.
                        </Alert>
                    )}

                    {errorAlert && (
                        <Alert severity="error" className="aggReportAlert">
                            <AlertTitle>Error</AlertTitle>
                            {errorText}
                        </Alert>
                    )}
                </div>

                <Dialog open={validationError} onClose={() => setValidationError(false)}>
                    <DialogTitle>Missing Information</DialogTitle>
                    <DialogContent>
                        <DialogContentText>{validationText}</DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setValidationError(false)}>OK</Button>
                    </DialogActions>
                </Dialog>
            </LocalizationProvider>
        </div>
    );
}

export default React.memo(AggReport);